/**
 * Camera Animator
 *
 * Provides smooth, time-based camera transitions using RAF.
 * Uses ease-out cubic easing and log-space zoom interpolation
 * for perceptually uniform zoom transitions.
 *
 * Abort detection: each frame reads deps.getCamera() and compares against
 * the value we wrote last frame. If they differ, an external source changed
 * the camera (user zoom/pan) and we abort immediately.
 */

import type { Camera } from "@rool/renderer/types";

// ============================================================================
// TYPES
// ============================================================================

export interface CameraAnimatorConfig {
	/** Animation duration in milliseconds (default: 600) */
	duration: number;
}

export interface CameraAnimatorDeps {
	/** Get current camera state */
	getCamera(): Camera;
	/** Set camera state */
	setCamera(camera: Camera): void;
}

export interface CameraAnimator {
	/** Start animating to a target camera. Replaces any in-progress animation. */
	animateTo(target: Camera, onComplete?: () => void): void;
	/** Stop animation at current position. Does NOT snap to target. */
	stop(): void;
	/** Whether an animation is currently running */
	isAnimating(): boolean;
	/** Stop and clean up */
	destroy(): void;
}

// ============================================================================
// DEFAULTS
// ============================================================================

const DEFAULT_CONFIG: CameraAnimatorConfig = {
	duration: 600,
};

// ============================================================================
// IMPLEMENTATION
// ============================================================================

/** Linear interpolation */
function lerp(a: number, b: number, t: number): number {
	return a + (b - a) * t;
}

/** Ease-out cubic: 1 - (1-t)^3 */
function easeOutCubic(t: number): number {
	return 1 - Math.pow(1 - t, 3);
}

function camerasEqual(a: Camera, b: Camera): boolean {
	return a.x === b.x && a.y === b.y && a.zoom === b.zoom;
}

export function createCameraAnimator(
	deps: CameraAnimatorDeps,
	initialConfig?: Partial<CameraAnimatorConfig>,
): CameraAnimator {
	const config: CameraAnimatorConfig = {
		...DEFAULT_CONFIG,
		...initialConfig,
	};

	let rafId: number | null = null;
	let animating = false;
	let startCamera: Camera = { x: 0, y: 0, zoom: 1 };
	let target: Camera = { x: 0, y: 0, zoom: 1 };
	let startTime: number = 0;
	let lastSetCamera: Camera | null = null;
	let onCompleteCallback: (() => void) | undefined;

	// Pre-computed log-space zoom values
	let logStartZoom: number = 0;
	let logEndZoom: number = 0;

	function animate(): void {
		if (!animating) return;

		// Abort detection: if external source changed camera since our last write
		const current = deps.getCamera();
		if (lastSetCamera && !camerasEqual(current, lastSetCamera)) {
			stopInternal();
			return;
		}

		const now = performance.now();
		const elapsed = now - startTime;
		const rawT = Math.min(elapsed / config.duration, 1);
		const t = easeOutCubic(rawT);

		if (rawT >= 1) {
			// Animation complete — snap to target
			deps.setCamera(target);
			lastSetCamera = target;
			const cb = onCompleteCallback;
			stopInternal();
			cb?.();
			return;
		}

		// Interpolate position linearly, zoom in log-space
		const newCamera: Camera = {
			x: lerp(startCamera.x, target.x, t),
			y: lerp(startCamera.y, target.y, t),
			zoom: Math.exp(lerp(logStartZoom, logEndZoom, t)),
		};

		deps.setCamera(newCamera);
		lastSetCamera = newCamera;

		rafId = requestAnimationFrame(animate);
	}

	function stopInternal(): void {
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		animating = false;
		lastSetCamera = null;
		onCompleteCallback = undefined;
	}

	return {
		animateTo(newTarget: Camera, onComplete?: () => void): void {
			// Stop any in-progress animation
			stopInternal();

			startCamera = deps.getCamera();
			target = newTarget;
			onCompleteCallback = onComplete;
			animating = true;
			startTime = performance.now();

			// Pre-compute log-space zoom endpoints
			logStartZoom = Math.log(startCamera.zoom);
			logEndZoom = Math.log(newTarget.zoom);

			// Record current camera as our "last set" so first frame doesn't false-abort
			lastSetCamera = startCamera;

			rafId = requestAnimationFrame(animate);
		},

		stop(): void {
			stopInternal();
		},

		isAnimating(): boolean {
			return animating;
		},

		destroy(): void {
			stopInternal();
		},
	};
}
